import {useState, useRef, useCallback, useEffect, FC } from 'react';
import { Icons, Alert, Table, EmptyStateIcons, showAlert, Icon, Button, ButtonStyles } from '@silverfort/sf-components';
import useGetColumns from './columns/useGetColumns';
import useGetRefSize from '../../../../hooks/useGetRefSize';
import { IncidentsConfiguration } from '../types';
import useSaveIncidentConfiguration from '../../api/mutations/useSaveIncidentConfiguration';
import IncidentConfigurationDrawer from './IncidentConfigurationDrawer';
import useGetIncidentsConfigurations from '../../api/queries/useGetIncidentsConfigurations';
import { useTowerLayout } from '../../../../TowerLayout/TowerLayoutProvider';
const successMessage = 'Configuration saved successfully';
const errorMessage = 'Failed to save configuration';

interface IncidentsConfigurationPageProps {
    onBackClick: () => void;
}

const IncidentsConfigurationPage: FC<IncidentsConfigurationPageProps> = ({ onBackClick }) => {
    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);

    const { isFetching, incidentsConfiguration, refetch } = useGetIncidentsConfigurations();
    const [selectedIncidentConfig, setSelectedIncidentConfig] = useState<IncidentsConfiguration | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setSelectedIncidentConfig(null);
    }, []);

    const onSuccess = useCallback(() => {
        showAlert(successMessage, 'success');
        refetch();
        closeDrawer();
    }, [closeDrawer, refetch]);

    const onError = useCallback(() => {
        showAlert(errorMessage, 'error');
    }, []);

    const { saveIncidentConfigurations, loading: isSaving } = useSaveIncidentConfiguration({
        onSuccess: onSuccess,
        onError
    });

    const { setLayoutHeader, setLayoutContentLeft, setLayoutContentIcon, clearLayout } = useTowerLayout();
    useEffect(() => {
        setLayoutHeader('Incidents Customization');
        setLayoutContentIcon(
            <Icon className="text-sf-gray-400" icon={Icons.Target} size={20} />
        );
        setLayoutContentLeft(
            <Button
                buttonStyle={ButtonStyles.GhostSolid}
                iconRight={Icons.ArrowBack}
                onClick={onBackClick}
            />
        );
        return clearLayout;
    }, [onBackClick, setLayoutContentLeft, setLayoutContentIcon, setLayoutHeader, clearLayout]);

    const openRow = useCallback((row: IncidentsConfiguration) => {
        setSelectedIncidentConfig(row);
        setIsDrawerOpen(true);
    }, []);

    const columns = useGetColumns(openRow);

    return (
        <div className="flex flex-col flex-1 h-full">
            <Alert />
            <div className="flex-grow flex-shrink p-6 w-full h-1" ref={tableRef}>
                <Table
                    loading={false}
                    columns={columns}
                    bodyHeight={tableHeight ? tableHeight - 56 : 0}
                    rows={incidentsConfiguration ?? []}
                    EmptyStateProps={{
                        className: 'w-full h-max',
                        title: 'No Incidents',
                        description: 'No incidents configurations available',
                        icon: EmptyStateIcons.TaskDone
                    }}
                    rowMargin={8}
                    rowClassName={`rounded-lg mb-4 border ${isFetching ? 'animate-pulse' : ''}`}
                    tableContainerClassName="w-full h-full"
                    keyExtractor={(row) => row.id}
                />
            </div>

            <IncidentConfigurationDrawer
                isOpen={isDrawerOpen && !!selectedIncidentConfig}
                onClose={closeDrawer}
                incidentConfiguration={selectedIncidentConfig}
                isSaving={isSaving}
                onSave={saveIncidentConfigurations}
            />

        </div>
    );
};

export default IncidentsConfigurationPage;
